import type { AdSyncSettingsDto, SyncRuleMode } from '@/api/types';

/**
 * Lógica pura de la tarjeta de reglas del sync del AD (licencia, inclusión de practicantes y baja de
 * practicantes). Va aparte del componente para poder probarla sin pintar nada.
 */

/**
 * Los valores con los que arranca el backend: licencia, incorporación y baja de practicantes apagadas, y la baja a
 * Frontline por licencia en solo reporte. Es lo que se muestra mientras el servidor no
 * devuelva `adSync` (un backend anterior al cambio, o una pestaña abierta durante el despliegue).
 */
export const AD_SYNC_POR_OMISION: AdSyncSettingsDto = {
  licenseMode: 'Off',
  allowedLicenseSkuIds: [],
  maxLicenseDiscardPercent: 10,
  practicanteInclusionMode: 'Off',
  practicanteDeactivationMode: 'Off',
  maxPracticanteDeactivationPercent: 30,
  licenseDemotionMode: 'ReportOnly',
  maxLicenseDemotionPercent: 10,
};

export const MODOS: readonly SyncRuleMode[] = ['Off', 'ReportOnly', 'Enforce'];

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export interface ProblemaDeSku {
  tipo: 'formato' | 'duplicado';
  valor: string;
}

export interface SkusLeidos {
  /** Los identificadores válidos, en minúsculas y sin repetir. */
  skus: string[];
  problemas: ProblemaDeSku[];
}

/**
 * Lee la lista de licencias que escribe la persona: un `SkuId` por línea (o separados por coma o espacio).
 *
 * 🔴 Se valida que sea un GUID porque el backend compara por `SkuId` y no por nombre: escribir «E3» no
 * empataría con ninguna licencia y las cuentas con esa licencia se descartarían en silencio.
 */
export function leerSkus(texto: string): SkusLeidos {
  const vistos = new Set<string>();
  const skus: string[] = [];
  const problemas: ProblemaDeSku[] = [];

  for (const crudo of texto.split(/[\s,;]+/)) {
    const valor = crudo.trim().toLowerCase();
    if (!valor) continue;
    if (!GUID.test(valor)) {
      problemas.push({ tipo: 'formato', valor: crudo.trim() });
      continue;
    }
    if (vistos.has(valor)) {
      problemas.push({ tipo: 'duplicado', valor });
      continue;
    }
    vistos.add(valor);
    skus.push(valor);
  }

  return { skus, problemas };
}

export function skusATexto(skus: readonly string[]): string {
  return skus.join('\n');
}

export function porcentajeValido(valor: number): boolean {
  return Number.isInteger(valor) && valor >= 1 && valor <= 100;
}

/** ¿Alguna de las reglas está en «Aplicar»? Es el caso que pide confirmar antes de guardar. */
export function hayAplicar(form: AdSyncSettingsDto): boolean {
  return [
    form.licenseMode, form.practicanteInclusionMode, form.practicanteDeactivationMode, form.licenseDemotionMode,
  ].includes('Enforce');
}

/**
 * La baja a Frontline por licencia solo corre con la licencia en «Aplicar»: solo ahí la regla de licencia deja fuera a
 * alguien de verdad. Con la licencia apagada o en solo reporte, esta regla no hace nada; la pantalla lo dice antes de
 * guardar.
 */
export function bajaFrontlineSinLicencia(form: AdSyncSettingsDto): boolean {
  return form.licenseDemotionMode !== 'Off' && form.licenseMode !== 'Enforce';
}

/**
 * La baja de practicantes solo corre con la inclusión en «Aplicar»: con ella apagada o en solo reporte,
 * los practicantes no están en el conjunto de la corrida, y compararlos contra él los daría de baja a
 * todos. El backend lo respeta y lo avisa en el registro; la pantalla lo dice antes de guardar.
 */
export function bajaSinInclusion(form: AdSyncSettingsDto): boolean {
  return form.practicanteDeactivationMode !== 'Off' && form.practicanteInclusionMode !== 'Enforce';
}

/** Con la licencia encendida, la lista no puede ir vacía: el servidor lo rechaza, y aquí se dice antes. */
export function licenciaSinLista(form: AdSyncSettingsDto): boolean {
  return form.licenseMode !== 'Off' && form.allowedLicenseSkuIds.length === 0;
}

export function sonIguales(a: AdSyncSettingsDto, b: AdSyncSettingsDto): boolean {
  return a.licenseMode === b.licenseMode
    && a.maxLicenseDiscardPercent === b.maxLicenseDiscardPercent
    && a.practicanteInclusionMode === b.practicanteInclusionMode
    && a.practicanteDeactivationMode === b.practicanteDeactivationMode
    && a.maxPracticanteDeactivationPercent === b.maxPracticanteDeactivationPercent
    && a.licenseDemotionMode === b.licenseDemotionMode
    && a.maxLicenseDemotionPercent === b.maxLicenseDemotionPercent
    && a.allowedLicenseSkuIds.length === b.allowedLicenseSkuIds.length
    && a.allowedLicenseSkuIds.every((sku, i) => sku === b.allowedLicenseSkuIds[i]);
}

export interface ErroresDelFormulario {
  skusConFormato: ProblemaDeSku[];
  isLicenseListMissing: boolean;
  isLicenseCapInvalid: boolean;
  isDeactivationCapInvalid: boolean;
  isDemotionCapInvalid: boolean;
  /** ¿Hay algo que impida guardar? */
  hasErrors: boolean;
}

export function validar(form: AdSyncSettingsDto, problemasDeSku: ProblemaDeSku[]): ErroresDelFormulario {
  const skusConFormato = problemasDeSku.filter(p => p.tipo === 'formato');
  const sinLista = licenciaSinLista(form);
  const topeLicencia = !porcentajeValido(form.maxLicenseDiscardPercent);
  const topeBaja = !porcentajeValido(form.maxPracticanteDeactivationPercent);
  const topeConversion = !porcentajeValido(form.maxLicenseDemotionPercent);
  return {
    skusConFormato,
    isLicenseListMissing: sinLista,
    isLicenseCapInvalid: topeLicencia,
    isDeactivationCapInvalid: topeBaja,
    isDemotionCapInvalid: topeConversion,
    hasErrors: skusConFormato.length > 0 || sinLista || topeLicencia || topeBaja || topeConversion,
  };
}
